import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Can from '../../components/Can';
import {
  Box,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TablePagination,
  TextField,
  useTheme,
  useMediaQuery,
  Stack,
  InputAdornment,
  Tooltip,
  Grid,
  CircularProgress,
  IconButton,
  Button,
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
} from '@mui/material';
import {
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  Add as AddIcon,
  Print as PrintIcon,
  Block as BlockIcon,
  MoreVert as MoreVertIcon,
  TaskAlt as TaskAltIcon,
  Checklist as ChecklistIcon,
  Close as CloseIcon,
  PlaylistRemove as PlaylistRemoveIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import ordenCompraService, { OrdenCompra, OrdenCompraSearchParams, esOrdenCompraAnulada } from '../../services/ordenCompraService';
import { Proveedor } from '../../services/proveedorService';
import ProveedorSearch from '../../components/ProveedorSearch';
import { formatCurrency, formatDate, formatProveedorConNro } from '../../utils/formatters';
import { getApiErrorMessage } from '../../utils/apiError';
import PDFViewer from '../../components/shared/PDFViewer';
import OrdenCompraAprobacionIndicador from '../../components/compras/OrdenCompraAprobacionIndicador';
import CancelarCantidadesOrdenCompraDialog from '../../components/compras/CancelarCantidadesOrdenCompraDialog';
import { useCan } from '../../hooks/useCan';
import {
  esOrdenCompraAprobada,
  getAprobarOrdenCompraErrorMessage,
  PERMISO_APROBAR_ORDEN_COMPRA,
  PERMISO_IMPRIMIR_ORDEN_COMPRA,
} from '../../utils/ordenCompraAprobacion';

const getFirstDayOfMonth = (): string => {
  const d = new Date();
  d.setDate(1);
  return d.toISOString().split('T')[0];
};

const getLastDayOfMonth = (): string => {
  const d = new Date();
  d.setMonth(d.getMonth() + 1);
  d.setDate(0);
  return d.toISOString().split('T')[0];
};

const formatearFecha = (fecha: Date | string | null | undefined): string => {
  if (!fecha) return '-';
  const d = typeof fecha === 'string' ? new Date(fecha) : fecha;
  if (isNaN(d.getTime())) return '-';
  return formatDate(d.toISOString().split('T')[0]);
};

const formatoComprobante = (letra?: string, nroSuc?: number, nroComprobante?: number): string => {
  const L = (letra ?? '').trim() || '-';
  const suc = String(nroSuc ?? 0).padStart(4, '0');
  const nro = String(nroComprobante ?? 0).padStart(8, '0');
  return `${L}-${suc}-${nro}`;
};

const OrdenesCompra: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [ordenes, setOrdenes] = useState<OrdenCompra[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [printingId, setPrintingId] = useState<number | null>(null);
  const [anulandoId, setAnulandoId] = useState<number | null>(null);
  const [aprobandoId, setAprobandoId] = useState<number | null>(null);
  const puedeAprobar = useCan(PERMISO_APROBAR_ORDEN_COMPRA);
  const puedeImprimir = useCan(PERMISO_IMPRIMIR_ORDEN_COMPRA);
  const [seleccionando, setSeleccionando] = useState(false);
  const [seleccionadas, setSeleccionadas] = useState<number[]>([]);
  const [aprobandoLote, setAprobandoLote] = useState(false);
  const [kebabAnchor, setKebabAnchor] = useState<{ el: HTMLElement; ordenId: number } | null>(null);
  const [openProveedorSearch, setOpenProveedorSearch] = useState(false);
  const [nombreProveedorFiltro, setNombreProveedorFiltro] = useState('');
  const [pdfPreview, setPdfPreview] = useState<{ url: string; nro: number; title: string } | null>(null);
  const [cancelarCantidadesOrden, setCancelarCantidadesOrden] = useState<OrdenCompra | null>(null);
  const pdfPreviewRef = useRef(pdfPreview);
  pdfPreviewRef.current = pdfPreview;
  const [filters, setFilters] = useState(() => {
    const desde = searchParams.get('fechaDesde');
    const hasta = searchParams.get('fechaHasta');
    return {
      searchTerm: '',
      fechaDesde: desde && /^\d{4}-\d{2}-\d{2}$/.test(desde) ? desde : getFirstDayOfMonth(),
      fechaHasta: hasta && /^\d{4}-\d{2}-\d{2}$/.test(hasta) ? hasta : getLastDayOfMonth(),
      nroProveedor: '',
      letra: '',
      nroSuc: '',
      nroComprobante: '',
      usuario: '',
    };
  });

  const fetchOrdenes = useCallback(
    async (pageNum: number = page, pageSize: number = rowsPerPage, filtros: typeof filters = filters) => {
      try {
        setLoading(true);
        const params: OrdenCompraSearchParams = {
          pageNumber: pageNum + 1,
          pageSize,
          searchTerm: filtros.searchTerm || undefined,
          fechaDesde: filtros.fechaDesde || undefined,
          fechaHasta: filtros.fechaHasta || undefined,
          nroProveedor: filtros.nroProveedor || undefined,
          letra: filtros.letra || undefined,
          nroSuc: filtros.nroSuc ? parseInt(filtros.nroSuc, 10) : undefined,
          nroComprobante: filtros.nroComprobante ? parseInt(filtros.nroComprobante, 10) : undefined,
          usuario: filtros.usuario || undefined,
        };
        const result = await ordenCompraService.getOrdenesCompra(params);
        setOrdenes(result.items || []);
        setTotalCount(result.totalCount || 0);
      } catch (error: unknown) {
        console.error('Error al cargar órdenes de compra:', error);
        const msg =
          error && typeof error === 'object' && 'response' in error
            ? (error as { response?: { data?: { message?: string } } }).response?.data?.message
            : 'Error al cargar órdenes de compra';
        enqueueSnackbar(msg || 'Error al cargar órdenes de compra', { variant: 'error' });
        setOrdenes([]);
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    },
    [enqueueSnackbar]
  );

  useEffect(() => {
    fetchOrdenes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const desde = searchParams.get('fechaDesde');
    const hasta = searchParams.get('fechaHasta');
    setFilters((f) => ({
      ...f,
      ...(desde && /^\d{4}-\d{2}-\d{2}$/.test(desde) ? { fechaDesde: desde } : {}),
      ...(hasta && /^\d{4}-\d{2}-\d{2}$/.test(hasta) ? { fechaHasta: hasta } : {}),
    }));
    setPage(0);
  }, [searchParams]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setPage(0);
      fetchOrdenes(0, rowsPerPage, filters);
    }, 400);
    return () => clearTimeout(timeoutId);
  }, [filters]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
    fetchOrdenes(newPage, rowsPerPage, filters);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    fetchOrdenes(0, newRowsPerPage, filters);
  };

  const handleBuscar = () => {
    setPage(0);
    fetchOrdenes(0, rowsPerPage, filters);
  };

  const handleProveedorSelect = (proveedor: Proveedor) => {
    setFilters((f) => ({ ...f, nroProveedor: String(proveedor.nroProveedor) }));
    setNombreProveedorFiltro((proveedor.nombre ?? '').trim());
  };

  const handleClosePdfPreview = () => {
    setPdfPreview((prev) => {
      if (prev) URL.revokeObjectURL(prev.url);
      return null;
    });
  };

  const handleAnular = async (ordenId: number) => {
    if (anulandoId != null) return;
    try {
      const check = await ordenCompraService.puedeAnularOrdenCompra(ordenId);
      if (!check.puedeAnular) {
        enqueueSnackbar(check.motivos[0] ?? 'No se puede anular esta orden de compra', { variant: 'warning' });
        return;
      }
      if (!window.confirm('¿Está seguro que desea anular esta orden de compra?')) return;

      setAnulandoId(ordenId);
      await ordenCompraService.anularOrdenCompra(ordenId);
      enqueueSnackbar('Orden de compra anulada correctamente', { variant: 'success' });
      await fetchOrdenes(page, rowsPerPage, filters);
    } catch (error: unknown) {
      enqueueSnackbar(getApiErrorMessage(error, 'Error al anular la orden de compra'), { variant: 'error' });
    } finally {
      setAnulandoId(null);
    }
  };

  const handleAprobar = async (orden: OrdenCompra) => {
    const ordenId = orden.nroFormulario;
    if (aprobandoId != null || !puedeAprobar || esOrdenCompraAnulada(orden) || esOrdenCompraAprobada(orden)) return;
    if (!window.confirm('¿Está seguro que desea aprobar esta orden de compra?')) return;
    try {
      setAprobandoId(ordenId);
      const updated = await ordenCompraService.aprobarOrdenCompra(ordenId);
      if (updated && typeof updated === 'object' && 'nroFormulario' in updated) {
        setOrdenes((prev) =>
          prev.map((o) =>
            o.nroFormulario === ordenId
              ? { ...o, fechaAprobacion: updated.fechaAprobacion, usuarioAprobacion: updated.usuarioAprobacion }
              : o
          )
        );
      } else {
        await fetchOrdenes(page, rowsPerPage, filters);
      }
      enqueueSnackbar('Orden de compra aprobada', { variant: 'success' });
    } catch (error: unknown) {
      enqueueSnackbar(getAprobarOrdenCompraErrorMessage(error), { variant: 'error', autoHideDuration: 8000 });
    } finally {
      setAprobandoId(null);
    }
  };

  const esPendienteAprobacion = (o: OrdenCompra) => !esOrdenCompraAnulada(o) && !esOrdenCompraAprobada(o);
  const pendientesAprobacion = ordenes.filter(esPendienteAprobacion);
  const hayPendientesAprobacion = pendientesAprobacion.length > 0;
  const modoSeleccion = seleccionando && puedeAprobar && hayPendientesAprobacion;

  useEffect(() => {
    const pendientesIds = new Set(ordenes.filter(esPendienteAprobacion).map((o) => o.nroFormulario));
    setSeleccionadas((prev) => prev.filter((id) => pendientesIds.has(id)));
    if (pendientesIds.size === 0) setSeleccionando(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ordenes]);

  const toggleSeleccion = (ordenId: number) => {
    setSeleccionadas((prev) => (prev.includes(ordenId) ? prev.filter((id) => id !== ordenId) : [...prev, ordenId]));
  };

  const todasSeleccionadas = hayPendientesAprobacion && seleccionadas.length === pendientesAprobacion.length;

  const toggleSeleccionTodas = () => {
    setSeleccionadas(todasSeleccionadas ? [] : pendientesAprobacion.map((o) => o.nroFormulario));
  };

  const handleCancelarSeleccion = () => {
    setSeleccionando(false);
    setSeleccionadas([]);
  };

  const handleAprobarSeleccionadas = async () => {
    if (aprobandoLote || !puedeAprobar || seleccionadas.length === 0) return;
    const ids = [...seleccionadas];
    const plural = ids.length === 1 ? 'la orden de compra seleccionada' : `las ${ids.length} órdenes de compra seleccionadas`;
    if (!window.confirm(`¿Está seguro que desea aprobar ${plural}?`)) return;

    setAprobandoLote(true);
    const aprobadas = new Map<number, Pick<OrdenCompra, 'fechaAprobacion' | 'usuarioAprobacion'>>();
    const errores: string[] = [];
    let recargar = false;
    for (const id of ids) {
      try {
        const updated = await ordenCompraService.aprobarOrdenCompra(id);
        if (updated && typeof updated === 'object' && 'nroFormulario' in updated) {
          aprobadas.set(id, { fechaAprobacion: updated.fechaAprobacion, usuarioAprobacion: updated.usuarioAprobacion });
        } else {
          recargar = true;
          aprobadas.set(id, {});
        }
      } catch (error: unknown) {
        errores.push(getAprobarOrdenCompraErrorMessage(error));
        const status = (error as { response?: { status?: number } })?.response?.status;
        if (status === 404 || status === 405) break;
      }
    }

    if (recargar) {
      await fetchOrdenes(page, rowsPerPage, filters);
    } else if (aprobadas.size > 0) {
      setOrdenes((prev) => prev.map((o) => (aprobadas.has(o.nroFormulario) ? { ...o, ...aprobadas.get(o.nroFormulario) } : o)));
    }
    setSeleccionadas((prev) => prev.filter((id) => !aprobadas.has(id)));
    setAprobandoLote(false);

    if (aprobadas.size > 0) {
      enqueueSnackbar(
        aprobadas.size === 1 ? '1 orden de compra aprobada' : `${aprobadas.size} órdenes de compra aprobadas`,
        { variant: 'success' },
      );
    }
    if (errores.length > 0) {
      enqueueSnackbar(
        `No se pudieron aprobar ${errores.length === 1 ? '1 orden' : `${errores.length} órdenes`}: ${errores[0]}`,
        { variant: 'error', autoHideDuration: 8000 },
      );
    } else {
      setSeleccionando(false);
    }
  };

  const renderCheckboxSeleccion = (o: OrdenCompra) =>
    esPendienteAprobacion(o) ? (
      <Checkbox
        size="small"
        checked={seleccionadas.includes(o.nroFormulario)}
        onChange={() => toggleSeleccion(o.nroFormulario)}
        disabled={aprobandoLote}
        inputProps={{ 'aria-label': 'Seleccionar orden para aprobar' }}
      />
    ) : null;

  const handleKebabOpen = (event: React.MouseEvent<HTMLElement>, ordenId: number) => {
    setKebabAnchor({ el: event.currentTarget, ordenId });
  };

  const handleKebabClose = () => setKebabAnchor(null);

  const currentKebabOrden = kebabAnchor
    ? ordenes.find((o) => o.nroFormulario === kebabAnchor.ordenId)
    : null;

  const handleComprobanteClick = (o: OrdenCompra) => {
    if (!(o.nroFormulario > 0)) return;
    navigate(`/compras/ordenes/${o.nroFormulario}`);
  };

  const comprobanteSx = {
    cursor: 'pointer',
    color: 'primary.main',
    fontWeight: 500,
    '&:hover': { textDecoration: 'underline' },
  };

  const renderAccionesOrden = (o: OrdenCompra) => (
    <>
      <Tooltip title="Ver">
        <IconButton
          size="small"
          onClick={() => navigate(`/compras/ordenes/${o.nroFormulario}`)}
          sx={{ color: '#1976d2' }}
        >
          <VisibilityIcon />
        </IconButton>
      </Tooltip>
      {!esOrdenCompraAnulada(o) && puedeImprimir && (
        <Tooltip title={esOrdenCompraAprobada(o) ? 'Vista previa PDF' : 'La orden de compra debe estar aprobada para poder imprimirla'}>
          <span>
            <IconButton
              size="small"
              onClick={() => handleImprimirPdf(o)}
              disabled={!esOrdenCompraAprobada(o) || printingId === o.nroFormulario}
              sx={{
                bgcolor: '#1976d2',
                color: '#fff',
                borderRadius: 1.5,
                width: 32,
                height: 32,
                '&:hover': { bgcolor: '#1565c0', transform: 'scale(1.1)' },
                transition: 'all 0.2s',
                '&.Mui-disabled': {
                  bgcolor: 'action.disabledBackground',
                  color: 'action.disabled',
                },
              }}
            >
              {printingId === o.nroFormulario ? (
                <CircularProgress size={18} sx={{ color: '#fff' }} />
              ) : (
                <PrintIcon fontSize="small" />
              )}
            </IconButton>
          </span>
        </Tooltip>
      )}
      {!esOrdenCompraAnulada(o) && (
        <Tooltip title="Más acciones">
          <IconButton
            size="small"
            onClick={(e) => handleKebabOpen(e, o.nroFormulario)}
            sx={{ color: '#1976d2' }}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </>
  );

  const renderKebabItems = (o: OrdenCompra) => {
    if (esOrdenCompraAnulada(o)) return null;

    return [
      puedeAprobar && !esOrdenCompraAprobada(o) && (
        <MenuItem
          key="aprobar"
          onClick={() => {
            handleKebabClose();
            void handleAprobar(o);
          }}
          disabled={aprobandoId === o.nroFormulario}
        >
          <ListItemIcon>
            <TaskAltIcon fontSize="small" color="success" />
          </ListItemIcon>
          <ListItemText>Aprobar orden de compra</ListItemText>
        </MenuItem>
      ),
      <MenuItem
        key="cancelar-cantidades"
        onClick={() => {
          handleKebabClose();
          setCancelarCantidadesOrden(o);
        }}
        disabled={!esOrdenCompraAprobada(o)}
      >
        <ListItemIcon>
          <PlaylistRemoveIcon fontSize="small" color="warning" />
        </ListItemIcon>
        <ListItemText
          primary="Cancelar cantidades"
          secondary={esOrdenCompraAprobada(o) ? undefined : 'Requiere OC aprobada'}
        />
      </MenuItem>,
      <MenuItem
        key="anular"
        onClick={() => {
          handleKebabClose();
          void handleAnular(o.nroFormulario);
        }}
        disabled={anulandoId === o.nroFormulario}
        sx={{ color: 'error.main' }}
      >
        <ListItemIcon>
          <BlockIcon fontSize="small" color="error" />
        </ListItemIcon>
        <ListItemText>Anular orden de compra</ListItemText>
      </MenuItem>,
    ];
  };

  const handleImprimirPdf = async (orden: OrdenCompra) => {
    const nroFormulario = orden.nroFormulario;
    if (!puedeImprimir || !esOrdenCompraAprobada(orden)) return;
    try {
      setPrintingId(nroFormulario);
      const blob = await ordenCompraService.getOrdenCompraPdf(nroFormulario);
      const url = URL.createObjectURL(blob);
      setPdfPreview((prev) => {
        if (prev) URL.revokeObjectURL(prev.url);
        return {
          url,
          nro: nroFormulario,
          title: `OC ${formatoComprobante(orden.letra, orden.nroSuc, orden.nroComprobante ?? undefined)}`,
        };
      });
    } catch (e) {
      console.error(e);
      enqueueSnackbar('No se pudo generar el PDF de la orden de compra', { variant: 'error' });
    } finally {
      setPrintingId(null);
    }
  };

  useEffect(() => {
    return () => {
      const p = pdfPreviewRef.current;
      if (p?.url) URL.revokeObjectURL(p.url);
    };
  }, []);

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh', maxWidth: '100%', overflowX: 'hidden' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'stretch', sm: 'center' }}
            spacing={2}
            sx={{ mb: 3 }}
          >
            <Typography variant="h4" sx={{ fontWeight: 600, color: '#1976d2', fontSize: { xs: '1.35rem', sm: '2.125rem' } }}>
              Órdenes de Compra
            </Typography>
            <Can permiso="compras.ordenes.nuevo">
            <Button
              variant="contained"
              fullWidth={isMobile}
              sx={{ bgcolor: '#1976d2', '&:hover': { bgcolor: '#1565c0' }, alignSelf: { xs: 'stretch', sm: 'center' } }}
              startIcon={<AddIcon />}
              onClick={() => navigate('/compras/ordenes/nuevo')}
            >
              Nueva OC
            </Button>
            </Can>
          </Stack>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                placeholder="Buscar por número, comprobante o nombre..."
                value={filters.searchTerm}
                onChange={(e) => setFilters((f) => ({ ...f, searchTerm: e.target.value }))}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#1976d2' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, backgroundColor: '#f8f9fa' } }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Fecha desde"
                value={filters.fechaDesde}
                onChange={(e) => setFilters((f) => ({ ...f, fechaDesde: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Fecha hasta"
                value={filters.fechaHasta}
                onChange={(e) => setFilters((f) => ({ ...f, fechaHasta: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={1}>
              <TextField
                fullWidth
                size="small"
                placeholder="Letra"
                value={filters.letra}
                onChange={(e) => setFilters((f) => ({ ...f, letra: e.target.value.toUpperCase() }))}
                inputProps={{ maxLength: 1 }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={1}>
              <TextField
                fullWidth
                size="small"
                type="number"
                placeholder="Suc"
                value={filters.nroSuc}
                onChange={(e) => setFilters((f) => ({ ...f, nroSuc: e.target.value }))}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={1}>
              <TextField
                fullWidth
                size="small"
                type="number"
                placeholder="Nro. Comp."
                value={filters.nroComprobante}
                onChange={(e) => setFilters((f) => ({ ...f, nroComprobante: e.target.value }))}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                label="Nro. proveedor"
                placeholder="Ej: 1001"
                value={filters.nroProveedor}
                onChange={(e) => {
                  const v = e.target.value.replace(/\D/g, '');
                  setFilters((f) => ({ ...f, nroProveedor: v }));
                  setNombreProveedorFiltro('');
                }}
                inputProps={{ inputMode: 'numeric', 'aria-label': 'Número de proveedor' }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title="Buscar proveedor por nombre">
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={() => setOpenProveedorSearch(true)}
                          aria-label="Buscar proveedor por nombre"
                          sx={{ color: '#1976d2' }}
                        >
                          <SearchIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
                helperText={nombreProveedorFiltro ? `Nombre: ${nombreProveedorFiltro}` : undefined}
                InputLabelProps={{ shrink: true }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                placeholder="Usuario"
                value={filters.usuario}
                onChange={(e) => setFilters((f) => ({ ...f, usuario: e.target.value }))}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Button
                variant="outlined"
                onClick={handleBuscar}
                sx={{ borderRadius: 2, color: '#1976d2', borderColor: '#1976d2' }}
              >
                Buscar
              </Button>
            </Grid>
            {puedeAprobar && hayPendientesAprobacion && (
              <Grid item xs={12} md>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  justifyContent="flex-end"
                  alignItems={{ xs: 'stretch', sm: 'center' }}
                  spacing={1}
                >
                  {modoSeleccion ? (
                    <>
                      <Button
                        variant="contained"
                        color="success"
                        startIcon={aprobandoLote ? <CircularProgress size={18} color="inherit" /> : <TaskAltIcon />}
                        onClick={() => void handleAprobarSeleccionadas()}
                        disabled={aprobandoLote || seleccionadas.length === 0}
                        sx={{ textTransform: 'none' }}
                      >
                        Aprobar seleccionadas ({seleccionadas.length})
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<CloseIcon />}
                        onClick={handleCancelarSeleccion}
                        disabled={aprobandoLote}
                        sx={{ textTransform: 'none' }}
                      >
                        Cancelar
                      </Button>
                    </>
                  ) : (
                    <Button
                      variant="outlined"
                      color="success"
                      startIcon={<ChecklistIcon />}
                      onClick={() => setSeleccionando(true)}
                      sx={{ textTransform: 'none' }}
                    >
                      Aprobar varias
                    </Button>
                  )}
                </Stack>
              </Grid>
            )}
          </Grid>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
              <CircularProgress sx={{ color: '#1976d2' }} />
            </Box>
          ) : isMobile ? (
            <>
              <Stack spacing={2}>
                {ordenes.map((o) => (
                  <Card
                    key={o.nroFormulario}
                    variant="outlined"
                    sx={{ p: 2, opacity: esOrdenCompraAnulada(o) ? 0.65 : 1 }}
                  >
                    <Stack spacing={1}>
                      <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                        {modoSeleccion && renderCheckboxSeleccion(o)}
                        <Typography
                          component="span"
                          variant="subtitle1"
                          sx={{ ...comprobanteSx, fontWeight: 600 }}
                          onClick={() => handleComprobanteClick(o)}
                        >
                          {formatoComprobante(o.letra, o.nroSuc, o.nroComprobante ?? undefined)}
                        </Typography>
                        {esOrdenCompraAnulada(o) && (
                          <Chip label="Anulada" size="small" color="default" variant="outlined" />
                        )}
                      </Stack>
                      <Typography variant="body2" color="text.secondary">
                        Fecha: {formatearFecha(o.fechaEmision)} | Proveedor: {formatProveedorConNro(o.nroProveedor, o.nombre)} | Total:{' '}
                        {formatCurrency(o.importeTotal ?? 0)}
                      </Typography>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography variant="body2" color="text.secondary">
                          Aprobada:
                        </Typography>
                        <OrdenCompraAprobacionIndicador orden={o} />
                      </Stack>
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        {renderAccionesOrden(o)}
                      </Stack>
                    </Stack>
                  </Card>
                ))}
              </Stack>
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 20]}
                labelRowsPerPage="Filas:"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count !== -1 ? count : to}`}
                sx={{ mt: 2 }}
              />
            </>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                    {modoSeleccion && (
                      <TableCell padding="checkbox">
                        <Checkbox
                          size="small"
                          checked={todasSeleccionadas}
                          indeterminate={seleccionadas.length > 0 && !todasSeleccionadas}
                          onChange={toggleSeleccionTodas}
                          disabled={aprobandoLote}
                          inputProps={{ 'aria-label': 'Seleccionar todas las órdenes pendientes de aprobación' }}
                        />
                      </TableCell>
                    )}
                    <TableCell sx={{ fontWeight: 600 }}>Fecha</TableCell>
                    <TableCell sx={{ fontWeight: 600, display: { xs: 'none', lg: 'table-cell' }, minWidth: 160, whiteSpace: 'nowrap' }}>Comprobante</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Proveedor</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      Total
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                      Aprobada
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>
                      Acciones
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {ordenes.map((o) => (
                    <TableRow
                      key={o.nroFormulario}
                      hover
                      sx={{ opacity: esOrdenCompraAnulada(o) ? 0.65 : 1 }}
                    >
                      {modoSeleccion && <TableCell padding="checkbox">{renderCheckboxSeleccion(o)}</TableCell>}
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <span>{formatearFecha(o.fechaEmision)}</span>
                          {esOrdenCompraAnulada(o) && (
                            <Chip label="Anulada" size="small" color="default" variant="outlined" />
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' }, whiteSpace: 'nowrap' }}>
                        <Typography
                          component="span"
                          sx={comprobanteSx}
                          onClick={() => handleComprobanteClick(o)}
                        >
                          {formatoComprobante(o.letra, o.nroSuc, o.nroComprobante ?? undefined)}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        {formatProveedorConNro(o.nroProveedor, o.nombre)}
                      </TableCell>
                      <TableCell align="right">{formatCurrency(o.importeTotal ?? 0)}</TableCell>
                      <TableCell align="center">
                        <OrdenCompraAprobacionIndicador orden={o} />
                      </TableCell>
                      <TableCell align="center">{renderAccionesOrden(o)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 20]}
                labelRowsPerPage="Filas:"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count !== -1 ? count : to}`}
              />
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <ProveedorSearch
        open={openProveedorSearch}
        onClose={() => setOpenProveedorSearch(false)}
        onSelect={handleProveedorSelect}
      />

      <Menu anchorEl={kebabAnchor?.el ?? null} open={Boolean(kebabAnchor)} onClose={handleKebabClose}>
        {currentKebabOrden ? renderKebabItems(currentKebabOrden) : null}
      </Menu>

      <CancelarCantidadesOrdenCompraDialog
        open={Boolean(cancelarCantidadesOrden)}
        nroFormulario={cancelarCantidadesOrden?.nroFormulario ?? null}
        titulo={
          cancelarCantidadesOrden
            ? `OC ${formatoComprobante(
                cancelarCantidadesOrden.letra,
                cancelarCantidadesOrden.nroSuc,
                cancelarCantidadesOrden.nroComprobante
              )} - ${cancelarCantidadesOrden.nombre || ''}`
            : undefined
        }
        onClose={() => setCancelarCantidadesOrden(null)}
      />

      {pdfPreview && (
        <PDFViewer
          pdfUrl={pdfPreview.url}
          onClose={handleClosePdfPreview}
          title={pdfPreview.title}
          documentId={`${pdfPreview.nro}`}
          documentType="ordenCompra"
          downloadFileName={`orden_compra_${pdfPreview.nro}.pdf`}
        />
      )}
    </Box>
  );
};

export default OrdenesCompra;
